import { ReactNode } from 'react';

import type {
    StepConfig2,
    StepHelpers,
    StepFooterHelpers,
    SimpleStepConfig,
    ConditionalStepConfig,
    FormStepConfig,
    ComponentStepConfig,
} from '../StepWizard2';
import { NextButton, BackButton } from '../components/NavigationButtons';

/**
 * Creates a simple step with automatic navigation
 */
export function createSimpleStep<TData = unknown>(config: SimpleStepConfig<TData>): StepConfig2<TData> {
    return {
        path: config.path,
        prevPath: config.prevPath,
        nextPath: config.nextPath,
        title: config.title,
        description: config.description,
        headerLeftElement: config.headerLeftElement,
        headerRightElement: config.headerRightElement,
        content: (helpers: StepHelpers<TData>) => {
            if (typeof config.content === 'function') {
                // Check if it's a function that expects helpers (has parameters)
                if (config.content.length > 0) {
                    // It's already a function that takes helpers
                    return (config.content as (helpers: StepHelpers<TData>) => ReactNode)(helpers);
                }
                // It's a React component (function with no parameters)
                const Component = config.content as React.ComponentType;
                return <Component />;
            }
            // It's a ReactNode
            return config.content as ReactNode;
        },
        validateOnMount: config.validateOnMount,
        initialData: config.initialData,
        confirmation: config.confirmation,
        hideDefaultFooter: config.hideDefaultFooter,
        hideNextButton: config.hideNextButton,
        slotProps: config.slotProps,
        onStepExit: config.onStepExit,
        footerLeftElement: config.prevPath
            ? ({ onPrevious }: StepFooterHelpers<TData>) => (
                  <BackButton title={config.prevButtonLabel} prevPath={config.prevPath} onClick={onPrevious} />
              )
            : undefined,
        footerRightElement:
            config.nextPath && !config.hideNextButton
                ? ({ onNext }: StepFooterHelpers<TData>) => (
                      <NextButton title={config.nextButtonLabel} nextPath={config.nextPath} onClick={onNext} />
                  )
                : undefined,
    };
}

/**
 * Creates a step with conditional navigation based on step data
 */
export function createConditionalStep<TData = unknown>(config: ConditionalStepConfig<TData>): StepConfig2<TData> {
    return {
        path: config.path,
        title: config.title,
        description: config.description,
        headerLeftElement: config.headerLeftElement,
        headerRightElement: config.headerRightElement,
        content: config.content,
        initialData: config.initialData,
        confirmation: config.confirmation,
        hideDefaultFooter: config.hideDefaultFooter,
        hideNextButton: config.hideNextButton,
        slotProps: config.slotProps,
        onStepExit: config.onStepExit,
        footerLeftElement: config.prevPath
            ? ({ onPrevious }: StepFooterHelpers<TData>) => (
                  <BackButton
                      title={config.prevButtonLabel}
                      prevPath={config.prevPath}
                      onClick={onPrevious}
                      buttonProps={config.prevButtonProps}
                  />
              )
            : undefined,
        footerRightElement: !config.hideNextButton
            ? ({ canProceed, stepData, onNext }: StepFooterHelpers<TData>) => {
                  const isValid = config.validateCondition ? config.validateCondition(stepData) : canProceed;
                  const nextPath = isValid ? config.getNextPath(stepData) : undefined;

                  return (
                      <NextButton
                          title={config.nextButtonLabel}
                          disabled={!isValid}
                          nextPath={nextPath}
                          onClick={onNext}
                      />
                  );
              }
            : undefined,
    };
}

/**
 * Creates a step that renders a component with props
 */
export function createComponentStep<TData = unknown>(config: ComponentStepConfig<any, TData>): StepConfig2<TData> {
    const Component = config.component;

    return {
        path: config.path,
        title: config.title,
        description: config.description,
        validateOnMount: config.validateOnMount,
        confirmation: config.confirmation,
        hideDefaultFooter: config.hideDefaultFooter,
        hideNextButton: config.hideNextButton,
        slotProps: config.slotProps,
        onStepExit: config.onStepExit,
        headerLeftElement: config.headerLeftElement,
        headerRightElement: config.headerRightElement,
        nextButtonLabel: config.nextButtonLabel,
        submitButtonLabel: config.submitButtonLabel,
        previousButtonLabel: config.prevButtonLabel,
        content: (helpers: StepHelpers<TData>) => {
            let props = config.componentProps || {};

            if (config.onPropsChange) {
                const modifiedProps = config.onPropsChange(props, helpers);
                if (modifiedProps) {
                    props = modifiedProps;
                }
            }

            return <Component {...props} />;
        },
        footerLeftElement: config.prevPath
            ? () => (
                  <BackButton
                      title={config.prevButtonLabel}
                      prevPath={config.prevPath}
                      buttonProps={config.prevButtonProps}
                  />
              )
            : undefined,
        footerRightElement:
            config.nextPath && !config.hideNextButton
                ? ({ canProceed, onNext }: StepFooterHelpers<TData>) => (
                      <NextButton
                          title={config.nextButtonLabel}
                          nextPath={config.nextPath}
                          disabled={!canProceed}
                          onClick={onNext}
                      />
                  )
                : undefined,
    };
}

/**
 * Creates a form step with validation
 */
export function createFormStep<TData = unknown>(config: FormStepConfig<TData>): StepConfig2<TData> {
    return {
        path: config.path,
        title: config.title,
        description: config.description,
        headerLeftElement: config.headerLeftElement,
        headerRightElement: config.headerRightElement,
        initialData: config.initialData,
        confirmation: config.confirmation,
        hideDefaultFooter: config.hideDefaultFooter,
        hideNextButton: config.hideNextButton,
        slotProps: config.slotProps,
        onStepExit: config.onStepExit,
        content: (helpers: StepHelpers<TData>) => {
            // Auto-setup validation if needed
            if (config.validateOnChange && !helpers.getStepData()) {
                helpers.setCanProceed(false);
            }

            return config.content(helpers);
        },
        footerLeftElement: config.prevPath
            ? () => (
                  <BackButton
                      title={config.prevButtonLabel}
                      prevPath={config.prevPath}
                      buttonProps={config.prevButtonProps}
                  />
              )
            : undefined,
        footerRightElement:
            config.nextPath && !config.hideNextButton
                ? ({ canProceed, onNext }: StepFooterHelpers<TData>) => (
                      <NextButton
                          title={config.nextButtonLabel}
                          nextPath={config.nextPath}
                          disabled={!canProceed}
                          onClick={onNext}
                      />
                  )
                : undefined,
    };
}
